import React, { useState, useCallback, useRef } from 'react';
import { User, Building, Users, Mail, LogOut, Edit3, Save, X, Settings2Icon, Key, Link2, Building2, UserIcon, Briefcase, Box, Plus, Database, Shield, Check } from 'lucide-react';
import LogoUpload from '../../components/LogoUpload';
import ApiKeysTab from '../../components/ApiKeysTab';
import IntegrationsTab from '../../components/IntegrationsTab';
import ObjectDefinitionSettings from '../../components/crm/ObjectDefinitionSettings';
import ManageCategories from '../../components/crm/ManageCategories';
import CreateObjectDefinitionModal from '../../components/crm/CreateObjectDefinitionModal';
import useSettings from '../../hooks/useSettings';

const Settings = () => {
  const [showCreateObjectModal, setShowCreateObjectModal] = useState(false);
  const [contentVisible, setContentVisible] = useState(true);
  const pendingTabRef = useRef(null);

  const {
    objectDefinitions,
    showDarkModeWarning,
    darkMode,
    orgSettings,
    userSettings,
    members,
    pendingInvites,
    pendingAccessRequests,
    inviteEmail,
    loading,
    isEditingUser,
    activeTab,
    user,
    organization,
    setShowDarkModeWarning,
    setOrgSettings,
    setUserSettings,
    setInviteEmail,
    setIsEditingUser,
    setActiveTab,
    handleDarkModeToggle,
    confirmDarkMode,
    handleUserUpdate,
    handleCancelUserEdit,
    handleOrgUpdate,
    handleInviteUser,
    handleRemoveMember,
    handleCancelInvitation,
    handleReviewAccessRequest,
    updateOrganization,
    fetchObjectDefinitions,
    logout,
  } = useSettings();

  const ICON_MAP = {
    'building-2': Building2,
    'user': UserIcon,
    'handshake': Briefcase,
    'box': Box,
  };

  const tabs = [
    { id: 'account', label: 'Account', icon: User },
    { id: 'organization', label: 'Organization', icon: Building },
    { id: 'members', label: 'Members', icon: Users },
    { id: 'api-keys', label: 'API Keys', icon: Key },
    { id: 'integrations', label: 'Integrations', icon: Link2 },
  ];

  const switchTab = useCallback((newTab) => {
    if (newTab === activeTab) return;
    setContentVisible(false);
    pendingTabRef.current = newTab;
    setTimeout(() => {
      setActiveTab(pendingTabRef.current);
      setContentVisible(true);
    }, 120);
  }, [activeTab, setActiveTab]);

  return (
    <>
      {/* Dark Mode Warning Modal */}
      {showDarkModeWarning && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="absolute inset-0" onClick={() => setShowDarkModeWarning(false)} />
          <div className="relative z-10 bg-white dark:bg-gray-900 rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden">
            <div className="px-6 pt-6 pb-4">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Dark Mode is experimental
              </h2>
            </div>
            <div className="px-6 pb-6">
              <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
                Dark mode is not fully complete yet. Some pages and features may not display correctly.
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed mt-3">
                <strong>Note:</strong> Dark mode only changes the design for <u>you</u> as a user.
                It does <u>not</u> affect your organization or your form respondents.
              </p>
              <div className="flex justify-end gap-3 mt-6">
                <button
                  className="px-4 py-2 text-sm font-medium rounded-lg border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                  onClick={() => setShowDarkModeWarning(false)}
                >
                  Cancel
                </button>
                <button
                  className="px-4 py-2 text-sm font-medium rounded-lg bg-brand-600 text-white hover:bg-brand-700 transition-colors"
                  onClick={confirmDarkMode}
                >
                  Enable Dark Mode
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-6xl mx-auto py-10 px-6">
        {/* Header */}
        <div className="mb-10">
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">Settings</h1>
          <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">
            Manage your account, organization, and team preferences.
          </p>
        </div>

        {/* Tabbed Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Left Sidebar - Vertical Tab Navigation */}
          <div className="lg:col-span-1 lg:sticky lg:top-6 lg:self-start">
            <nav className="space-y-1">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    onClick={() => switchTab(tab.id)}
                    className={`w-full text-left px-4 py-2.5 rounded-lg transition-all flex items-center gap-3 text-sm active:scale-[0.97] ${
                      isActive
                        ? 'bg-gray-100/70 dark:bg-gray-700/40 text-gray-900 dark:text-gray-100 font-medium'
                        : 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    <span>{tab.label}</span>
                  </button>
                );
              })}
            </nav>

            {/* Data Model Section */}
            <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between px-4 mb-2">
                <span className="text-xs font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wider">Objects</span>
                <button
                  onClick={() => setShowCreateObjectModal(true)}
                  className="p-1 rounded-md text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  title="New object"
                >
                  <Plus className="w-3.5 h-3.5" />
                </button>
              </div>
              <nav className="space-y-0.5">
                {objectDefinitions.map((objDef) => {
                  const ObjIcon = ICON_MAP[objDef.icon] || Database;
                  const isActive = activeTab === `object-${objDef.id}`;
                  return (
                    <div key={objDef.id}>
                      <button
                        onClick={() => switchTab(`object-${objDef.id}`)}
                        className={`w-full text-left px-4 py-2 rounded-lg transition-all flex items-center gap-3 text-sm active:scale-[0.97] ${
                          isActive
                            ? 'bg-gray-100/70 dark:bg-gray-700/40 text-gray-900 dark:text-gray-100 font-medium'
                            : 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800'
                        }`}
                      >
                        <ObjIcon className="w-4 h-4" />
                        <span>{objDef.name}</span>
                        {objDef.is_system && (
                          <span className="ml-auto text-[10px] font-medium text-gray-400 dark:text-gray-500">System</span>
                        )}
                      </button>
                      {isActive && (
                        <div className="ml-7 mt-0.5 space-y-0.5">
                          {[
                            { id: 'fields', label: 'Fields' },
                            // Future: { id: 'record-layout', label: 'Record Layout' },
                          ].map((section) => (
                            <button
                              key={section.id}
                              onClick={() => {
                                document.getElementById(`section-${section.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                              }}
                              className="w-full text-left px-3 py-1.5 rounded-md text-xs text-gray-400 dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-all active:scale-[0.97]"
                            >
                              {section.label}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </nav>
            </div>
          </div>

          {/* Main Content */}
          <div className={`lg:col-span-3 transition-opacity duration-100 ${contentVisible ? 'opacity-100' : 'opacity-95'}`}>
            {/* Account Tab */}
            {activeTab === 'account' && (
              <div className="space-y-8">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Account Settings</h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    Manage your personal account information and preferences.
                  </p>
                </div>

                {/* Profile Information Card */}
                <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 overflow-hidden">
                  <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <User className="h-4 w-4 text-gray-400" />
                        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Profile Information</h3>
                      </div>
                      {!isEditingUser && (
                        <button
                          onClick={() => setIsEditingUser(true)}
                          className="text-sm text-brand-600 hover:text-brand-700 dark:text-brand-400 dark:hover:text-brand-300 font-medium flex items-center gap-1.5 transition-colors"
                        >
                          <Edit3 className="h-3.5 w-3.5" />
                          Edit
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="p-6">
                    {isEditingUser ? (
                      <form onSubmit={handleUserUpdate} className="space-y-5">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                              Email
                            </label>
                            <div className="px-3.5 py-2.5 border border-gray-200 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-900 text-sm text-gray-500 dark:text-gray-400">
                              {user?.email}
                            </div>
                            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">Email cannot be changed</p>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                              Username
                            </label>
                            <input
                              type="text"
                              value={userSettings.username}
                              onChange={(e) => setUserSettings({ ...userSettings, username: e.target.value })}
                              className="block w-full px-3.5 py-2.5 text-sm border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 transition-colors"
                              placeholder="Enter username"
                              required
                            />
                          </div>
                          <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                              Full Name
                            </label>
                            <input
                              type="text"
                              value={userSettings.full_name}
                              onChange={(e) => setUserSettings({ ...userSettings, full_name: e.target.value })}
                              className="block w-full px-3.5 py-2.5 text-sm border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 transition-colors"
                              placeholder="Enter your full name"
                            />
                          </div>
                        </div>
                        <div className="flex justify-end gap-2 pt-2">
                          <button
                            type="button"
                            onClick={handleCancelUserEdit}
                            className="px-4 py-2 text-sm font-medium rounded-lg border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors flex items-center gap-1.5"
                          >
                            <X className="h-3.5 w-3.5" />
                            Cancel
                          </button>
                          <button
                            type="submit"
                            disabled={loading}
                            className="px-4 py-2 text-sm font-medium rounded-lg bg-brand-600 text-white hover:bg-brand-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-1.5"
                          >
                            <Save className="h-3.5 w-3.5" />
                            {loading ? 'Saving...' : 'Save Changes'}
                          </button>
                        </div>
                      </form>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                        <div>
                          <label className="block text-xs font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wide mb-1.5">
                            Email
                          </label>
                          <p className="text-sm text-gray-900 dark:text-gray-100">{user?.email}</p>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wide mb-1.5">
                            Username
                          </label>
                          <p className="text-sm text-gray-900 dark:text-gray-100">{user?.username}</p>
                        </div>
                        <div className="md:col-span-2">
                          <label className="block text-xs font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wide mb-1.5">
                            Full Name
                          </label>
                          <p className="text-sm text-gray-900 dark:text-gray-100">{user?.full_name || 'Not set'}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Preferences Card */}
                <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 overflow-hidden">
                  <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700">
                    <div className="flex items-center gap-2">
                      <Settings2Icon className="h-4 w-4 text-gray-400" />
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Preferences</h3>
                    </div>
                  </div>
                  <div className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100">Dark Mode</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                          Switch between light and dark interface
                        </p>
                      </div>
                      <button
                        type="button"
                        aria-pressed={darkMode}
                        onClick={handleDarkModeToggle}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 ${
                          darkMode ? 'bg-brand-600' : 'bg-gray-200 dark:bg-gray-600'
                        }`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform ${
                            darkMode ? 'translate-x-5' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Sign Out Card */}
                <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 overflow-hidden">
                  <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700">
                    <div className="flex items-center gap-2">
                      <LogOut className="h-4 w-4 text-gray-400" />
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Account Actions</h3>
                    </div>
                  </div>
                  <div className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100">Sign Out</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                          Sign out of your account on this device
                        </p>
                      </div>
                      <button
                        onClick={logout}
                        className="px-4 py-2 text-sm font-medium rounded-lg text-rose-600 dark:text-rose-400 border border-rose-200 dark:border-rose-800 hover:bg-rose-50 dark:hover:bg-rose-900/20 transition-colors"
                      >
                        Sign Out
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Organization Tab */}
            {activeTab === 'organization' && (
              <div className="space-y-8">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Organization Settings</h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    Manage settings for <span className="font-medium text-gray-700 dark:text-gray-300">{organization?.name}</span>.
                  </p>
                </div>

                {/* Organization Details Card */}
                <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 overflow-hidden">
                  <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700">
                    <div className="flex items-center gap-2">
                      <Building className="h-4 w-4 text-gray-400" />
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Organization Details</h3>
                    </div>
                  </div>
                  <div className="p-6">
                    <form onSubmit={handleOrgUpdate} className="space-y-5">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                          Organization Name
                        </label>
                        <input
                          type="text"
                          value={orgSettings.name}
                          onChange={(e) => setOrgSettings({ ...orgSettings, name: e.target.value })}
                          className="block w-full px-3.5 py-2.5 text-sm border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 transition-colors"
                          placeholder="Enter organization name"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                          Description
                        </label>
                        <textarea
                          value={orgSettings.description}
                          onChange={(e) => setOrgSettings({ ...orgSettings, description: e.target.value })}
                          rows={3}
                          className="block w-full px-3.5 py-2.5 text-sm border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 transition-colors resize-none"
                          placeholder="Describe your organization"
                        />
                      </div>
                      <div className="flex justify-end pt-2">
                        <button
                          type="submit"
                          disabled={loading}
                          className="px-4 py-2 text-sm font-medium rounded-lg bg-brand-600 text-white hover:bg-brand-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          {loading ? 'Updating...' : 'Update Organization'}
                        </button>
                      </div>
                    </form>
                  </div>
                </div>

                {/* Logo Upload */}
                <LogoUpload
                  organization={organization}
                  onLogoUpdate={(updatedOrg) => updateOrganization(updatedOrg)}
                  isOwner={organization?.owner_id === user?.id}
                />

                {/* Manage Categories */}
                <ManageCategories
                  organization={organization}
                  objectDefinitions={objectDefinitions}
                  isOwner={organization?.owner_id === user?.id}
                  onUpdate={async () => {
                    await updateOrganization();
                  }}
                />
              </div>
            )}

            {/* Members Tab */}
            {activeTab === 'members' && (
              <div className="space-y-8">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Members & Invites</h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    Manage team members for <span className="font-medium text-gray-700 dark:text-gray-300">{organization?.name}</span>.
                  </p>
                </div>

                {/* Invite Users Card */}
                <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 overflow-hidden">
                  <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700">
                    <div className="flex items-center gap-2">
                      <Mail className="h-4 w-4 text-gray-400" />
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Invite New Members</h3>
                    </div>
                  </div>
                  <div className="p-6">
                    <form onSubmit={handleInviteUser} className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                          Email Address
                        </label>
                        <input
                          type="email"
                          value={inviteEmail}
                          onChange={(e) => setInviteEmail(e.target.value)}
                          placeholder="colleague@company.com"
                          required
                          className="block w-full px-3.5 py-2.5 text-sm border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 transition-colors"
                        />
                      </div>
                      <button
                        type="submit"
                        disabled={loading || !inviteEmail.trim()}
                        className="w-full px-4 py-2.5 text-sm font-medium rounded-lg bg-brand-600 text-white hover:bg-brand-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        {loading ? 'Sending Invite...' : 'Send Invitation'}
                      </button>
                    </form>
                  </div>
                </div>

                {/* Organization Members Card */}
                <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 overflow-hidden">
                  <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700">
                    <div className="flex items-center gap-2">
                      <Users className="h-4 w-4 text-gray-400" />
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                        Current Members
                      </h3>
                      <span className="text-xs text-gray-400 dark:text-gray-500 ml-1">
                        ({members.length})
                      </span>
                    </div>
                  </div>
                  <div>
                    {members.length === 0 ? (
                      <div className="p-8 text-center">
                        <Users className="h-8 w-8 text-gray-200 dark:text-gray-600 mx-auto mb-2" />
                        <p className="text-sm text-gray-500 dark:text-gray-400">No members found</p>
                      </div>
                    ) : (
                      <div className="divide-y divide-gray-50 dark:divide-gray-700/50">
                        {members.map((member) => (
                          <div key={member.id} className="px-6 py-4 flex items-center justify-between hover:bg-gray-50/50 dark:hover:bg-gray-700/20 transition-colors">
                            <div className="flex items-center gap-3.5">
                              <div className="w-9 h-9 bg-brand-50 dark:bg-brand-900/30 rounded-full flex items-center justify-center">
                                <span className="text-sm font-medium text-brand-600 dark:text-brand-400">
                                  {(member.full_name || member.username || '?').charAt(0).toUpperCase()}
                                </span>
                              </div>
                              <div>
                                <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                  {member.full_name || member.username}
                                </p>
                                <p className="text-xs text-gray-500 dark:text-gray-400">
                                  {member.email}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-3">
                              <span
                                className={`px-2.5 py-1 text-xs font-medium rounded-full ${
                                  organization?.owner_id === member.id
                                    ? 'bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300'
                                    : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
                                }`}
                              >
                                {organization?.owner_id === member.id ? 'Owner' : 'Member'}
                              </span>
                              {organization?.owner_id === user?.id && organization?.owner_id !== member.id && (
                                <button
                                  onClick={() => handleRemoveMember(member.id)}
                                  className="text-xs text-rose-500 dark:text-rose-400 hover:text-rose-700 dark:hover:text-rose-300 font-medium transition-colors"
                                >
                                  Remove
                                </button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Pending Invitations Card */}
                {pendingInvites.length > 0 && (
                  <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 overflow-hidden">
                    <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700">
                      <div className="flex items-center gap-2">
                        <Mail className="h-4 w-4 text-gray-400" />
                        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                          Pending Invitations
                        </h3>
                        <span className="text-xs text-gray-400 dark:text-gray-500 ml-1">
                          ({pendingInvites.length})
                        </span>
                      </div>
                    </div>
                    <div className="divide-y divide-gray-50 dark:divide-gray-700/50">
                      {pendingInvites.map((invite) => (
                        <div key={invite.id} className="px-6 py-4 flex items-center justify-between hover:bg-gray-50/50 dark:hover:bg-gray-700/20 transition-colors">
                          <div className="flex items-center gap-3.5">
                            <div className="w-9 h-9 bg-amber-50 dark:bg-amber-900/30 rounded-full flex items-center justify-center">
                              <Mail className="w-4 h-4 text-amber-500 dark:text-amber-400" />
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                {invite.email}
                              </p>
                              <p className="text-xs text-gray-500 dark:text-gray-400">
                                Invited by {invite.invited_by?.full_name || invite.invited_by?.username} · Expires{' '}
                                {new Date(invite.expires_at).toLocaleDateString()}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className="px-2.5 py-1 text-xs font-medium rounded-full bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300">
                              Pending
                            </span>
                            {organization?.owner_id === user?.id && (
                              <button
                                onClick={() => handleCancelInvitation(invite.id)}
                                className="text-xs text-rose-500 dark:text-rose-400 hover:text-rose-700 dark:hover:text-rose-300 font-medium transition-colors"
                              >
                                Cancel
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Staff Access Requests Card — only shown to org owner when there are pending requests */}
                {organization?.owner_id === user?.id && pendingAccessRequests.length > 0 && (
                  <div className="bg-white dark:bg-gray-800 rounded-xl border border-indigo-200 dark:border-indigo-700 overflow-hidden">
                    <div className="px-6 py-4 border-b border-indigo-100 dark:border-indigo-700 bg-indigo-50/50 dark:bg-indigo-900/20">
                      <div className="flex items-center gap-2">
                        <Shield className="h-4 w-4 text-indigo-500 dark:text-indigo-400" />
                        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                          Staff Access Requests
                        </h3>
                        <span className="text-xs text-indigo-500 dark:text-indigo-400 ml-1">
                          ({pendingAccessRequests.length})
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        A member of our staff is requesting temporary access to help with your organisation.
                      </p>
                    </div>
                    <div className="divide-y divide-gray-50 dark:divide-gray-700/50">
                      {pendingAccessRequests.map((req) => (
                        <div key={req.id} className="px-6 py-4 hover:bg-gray-50/50 dark:hover:bg-gray-700/20 transition-colors">
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex items-start gap-3.5 flex-1 min-w-0">
                              <div className="w-9 h-9 bg-indigo-50 dark:bg-indigo-900/30 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                                <Shield className="w-4 h-4 text-indigo-500 dark:text-indigo-400" />
                              </div>
                              <div className="min-w-0">
                                <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                  {req.superadmin_name || req.superadmin_email}
                                </p>
                                <p className="text-xs text-gray-500 dark:text-gray-400">
                                  {req.superadmin_email} · Requesting {req.requested_days} days ·{' '}
                                  {new Date(req.created_at).toLocaleDateString()}
                                </p>
                                {req.message && (
                                  <p className="text-sm text-gray-700 dark:text-gray-300 mt-2 px-3 py-2 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-100 dark:border-gray-600">
                                    "{req.message}"
                                  </p>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0 mt-0.5">
                              <button
                                onClick={() => handleReviewAccessRequest(req.id, 'deny')}
                                className="px-3 py-1.5 text-xs font-medium text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-900/20 rounded-lg transition-colors"
                              >
                                Deny
                              </button>
                              <button
                                onClick={() => handleReviewAccessRequest(req.id, 'approve')}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg transition-colors"
                              >
                                <Check className="w-3 h-3" />
                                Approve
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* API Keys Tab */}
            {activeTab === 'api-keys' && (
              <ApiKeysTab organization={organization} />
            )}

            {/* Integrations Tab */}
            {activeTab === 'integrations' && (
              <IntegrationsTab />
            )}

            {/* Object Definition Tabs */}
            {activeTab.startsWith('object-') && (
              <ObjectDefinitionSettings
                objectDefinitionId={activeTab.replace('object-', '')}
                objectDefinitions={objectDefinitions}
                onObjectDeleted={() => {
                  setActiveTab('account');
                  fetchObjectDefinitions();
                }}
                onObjectUpdated={fetchObjectDefinitions}
              />
            )}
          </div>
        </div>
      </div>

      {showCreateObjectModal && (
        <CreateObjectDefinitionModal
          onClose={() => setShowCreateObjectModal(false)}
          onCreated={(newObj) => {
            setShowCreateObjectModal(false);
            fetchObjectDefinitions();
            setActiveTab(`object-${newObj.id}`);
          }}
        />
      )}
    </>
  );
};

export default Settings;
